import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProfile } from '../../services/profileService';

interface ProfileDropdownProps {
  profiles: FormProfile[];
  selectedProfileId: string | null;
  onSelectProfile: (profileId: string) => void;
  isLoading?: boolean;
}

/**
 * Dropdown component for loading saved profiles
 * Shows profile list with selected indicator and keyboard navigation
 */
const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  profiles,
  selectedProfileId,
  onSelectProfile,
  isLoading = false
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;

      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, profiles.length - 1));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < profiles.length) {
          onSelectProfile(profiles[focusedIndex].id);
          setIsOpen(false);
          setFocusedIndex(-1);
        }
        break;

      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;

      case 'End':
        e.preventDefault();
        setFocusedIndex(profiles.length - 1);
        break;
    }
  };

  // Scroll focused item into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuRef.current) {
      const focusedElement = menuRef.current.children[focusedIndex] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex, isOpen]);

  const selectedProfile = profiles.find((p) => p.id === selectedProfileId);

  const getButtonText = () => {
    if (isLoading) return 'Loading...';
    if (profiles.length === 0) return 'No profiles saved';
    if (selectedProfile) return selectedProfile.profile_name;
    return `Load Profile (${profiles.length})`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={profiles.length === 0 || isLoading}
        className={`inline-flex items-center justify-between gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
          profiles.length === 0 || isLoading
            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#1d3a8b] focus:ring-offset-2'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Load saved profile"
      >
        <span className="material-icons" style={{ fontSize: '18px' }}>
          folder_open
        </span>
        <span className="whitespace-nowrap">{getButtonText()}</span>
        <span className="material-icons" style={{ fontSize: '18px' }}>
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {/* Dropdown menu */}
      {isOpen && profiles.length > 0 && (
        <div
          ref={menuRef}
          role="listbox"
          className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto"
        >
          {/* Profile list */}
          <div className="py-1">
            {profiles.map((profile, index) => {
              const isSelected = profile.id === selectedProfileId;
              const isFocused = index === focusedIndex;

              return (
                <button
                  key={profile.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onSelectProfile(profile.id);
                    setIsOpen(false);
                    setFocusedIndex(-1);
                  }}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
                    isFocused
                      ? 'bg-blue-50'
                      : isSelected
                      ? 'bg-gray-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${
                        isSelected ? 'text-[#1d3a8b]' : 'text-gray-900'
                      }`}
                    >
                      {profile.profile_name}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {new Date(profile.updated_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  {isSelected && (
                    <span className="material-icons text-[#1d3a8b] flex-shrink-0" style={{ fontSize: '20px' }}>
                      check
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Manage All Profiles Link */}
          <div className="border-t border-gray-200 py-1">
            <button
              type="button"
              className="w-full text-left px-4 py-2 text-sm text-[#1d3a8b] hover:bg-gray-50 transition-colors flex items-center gap-2"
              onClick={() => {
                navigate('/ai-sequence/profiles');
                setIsOpen(false);
              }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>
                settings
              </span>
              <span>Manage All Profiles</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
